/**
 * Contratos tipados do Admin PWA (Frente E).
 *
 * Estes tipos representam o "cenário de teste" descrito na regra 5 do
 * protocolo de execução paralela (docs/parallel-execution.md): a UI do
 * Admin pode avançar com fixtures tipadas antes de `apps/api` existir.
 * Quando a API real estiver disponível, estes mesmos tipos devem ser
 * satisfeitos pelo adapter real — a fixture então passa a ser usada
 * apenas como cenário de teste (unit/e2e), nunca removida.
 *
 * Decisão: não importamos `packages/schemas` nem `packages/event-contracts`
 * para validação porque, na data desta entrega, `packages/schemas` ainda
 * não exporta nada (apenas README) e não há dependência `zod` instalada
 * no monorepo (ver package.json raiz). Para não introduzir uma dependência
 * nova sem necessidade (e sem ADR), a validação é feita com funções de
 * asserção manuais, no mesmo estilo de `assertEventEnvelope` em
 * `packages/event-contracts/src/index.ts`. Se `packages/schemas` publicar
 * um validador Zod reutilizável, substituir as funções `assert*` abaixo
 * por aquele validador é um próximo passo seguro e independente.
 */
export function assertConversationListItem(value) {
    if (!value.id || !value.customerId || !value.customerName) {
        throw new Error("Invalid ConversationListItem: missing identity fields");
    }
    if (value.leadScore < 0 || value.leadScore > 100) {
        throw new Error("Invalid ConversationListItem: leadScore out of range 0-100");
    }
    if (value.unreadCount < 0) {
        throw new Error("Invalid ConversationListItem: unreadCount cannot be negative");
    }
}
export function assertCustomer360(value) {
    if (!value.customerId || !value.name) {
        throw new Error("Invalid Customer360: missing identity fields");
    }
    if (value.leadScore < 0 || value.leadScore > 100) {
        throw new Error("Invalid Customer360: leadScore out of range 0-100");
    }
    if (value.totalOrders < 0 || value.totalSpentCents < 0) {
        throw new Error("Invalid Customer360: negative totals are not allowed");
    }
}
export function assertConversationDetail(value) {
    if (!value.id || !value.customerId || !value.customerName) {
        throw new Error("Invalid ConversationDetail: missing identity fields");
    }
    if (value.messages.length === 0) {
        throw new Error("Invalid ConversationDetail: messages cannot be empty");
    }
    for (const message of value.messages) {
        // Uma mensagem com `aiDraft` representa uma resposta da IA ainda não
        // enviada (preview, item 137): `text` pode ficar vazio nesse caso, pois
        // o texto a exibir está em `aiDraft.text`.
        if (!message.id || (!message.text && !message.aiDraft)) {
            throw new Error("Invalid ConversationDetail: message missing id/text");
        }
        if (message.aiDraft &&
            (message.aiDraft.confidencePercent < 0 || message.aiDraft.confidencePercent > 100)) {
            throw new Error("Invalid ConversationDetail: aiDraft confidencePercent out of range 0-100");
        }
    }
}
export function assertLeadScoreDetail(value) {
    if (!value.customerId || !value.customerName) {
        throw new Error("Invalid LeadScoreDetail: missing identity fields");
    }
    if (value.currentScore < 0 || value.currentScore > 100) {
        throw new Error("Invalid LeadScoreDetail: currentScore out of range 0-100");
    }
    for (const event of value.history) {
        if (event.previousScore < 0 || event.previousScore > 100 || event.newScore < 0 || event.newScore > 100) {
            throw new Error("Invalid LeadScoreDetail: history score out of range 0-100");
        }
    }
}
export function assertCommandCenterStats(value) {
    if (value.openConversations < 0 || value.pendingHumanTakeover < 0) {
        throw new Error("Invalid CommandCenterStats: negative counters are not allowed");
    }
    if (value.averageLeadScore < 0 || value.averageLeadScore > 100) {
        throw new Error("Invalid CommandCenterStats: averageLeadScore out of range 0-100");
    }
    if (value.aiResolutionRatePercent < 0 || value.aiResolutionRatePercent > 100) {
        throw new Error("Invalid CommandCenterStats: aiResolutionRatePercent out of range 0-100");
    }
}
